export type OnboardingConnectionFeedback = {
  message: string;
  type: "info" | "error";
  duration?: number;
};

export const CONNECTION_NOT_COMPLETED_TOAST_DURATION_MS = 7000;

type ResolveOnboardingConnectionFeedbackOptions = {
  error: unknown;
  fallbackMessage: string;
  notCompletedMessage: string;
  useInformationalExistingConnectionFeedback?: boolean;
};

const CONNECTION_NOT_COMPLETED_PATTERNS = [
  /modal blocked by browser/i,
  /connection (?:was )?cancell?ed/i,
  /connection dialog was closed/i,
  /authorization (?:was )?cancell?ed/i,
  /authorization (?:window )?was closed/i,
  /window was closed before .*connection/i,
];

const ALREADY_CONNECTED_TO_CURRENT_WORKSPACE_PATTERNS = [
  /already connected to (?:this|the current) workspace/i,
  /already connected in (?:this|the current) workspace/i,
];

const getConnectionErrorMessage = (error: unknown): string => {
  const sanitizeProviderBrand = (message: string) =>
    message.replace(/Nango/gi, "Bustly");

  if (error instanceof Error) {
    return sanitizeProviderBrand(error.message.trim());
  }

  return typeof error === "string" ? sanitizeProviderBrand(error.trim()) : "";
};

export const resolveOnboardingConnectionFeedback = ({
  error,
  fallbackMessage,
  notCompletedMessage,
  useInformationalExistingConnectionFeedback = false,
}: ResolveOnboardingConnectionFeedbackOptions): OnboardingConnectionFeedback => {
  const errorMessage = getConnectionErrorMessage(error);
  const connectionWasNotCompleted = CONNECTION_NOT_COMPLETED_PATTERNS.some(
    (pattern) => pattern.test(errorMessage),
  );

  if (connectionWasNotCompleted) {
    return {
      message: notCompletedMessage,
      type: "info",
      duration: CONNECTION_NOT_COMPLETED_TOAST_DURATION_MS,
    };
  }

  const connectionAlreadyExistsInCurrentWorkspace =
    useInformationalExistingConnectionFeedback &&
    ALREADY_CONNECTED_TO_CURRENT_WORKSPACE_PATTERNS.some((pattern) =>
      pattern.test(errorMessage),
    );

  if (connectionAlreadyExistsInCurrentWorkspace) {
    return {
      message: errorMessage,
      type: "info",
      duration: CONNECTION_NOT_COMPLETED_TOAST_DURATION_MS,
    };
  }

  return {
    message: errorMessage || fallbackMessage,
    type: "error",
  };
};
